import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Charts
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

import axios from "axios";
import API_ENDPOINTS from "config/api";

export default function DashboardContabilidad() {
  const [stats, setStats] = useState({
    totalActivos: 0,
    totalPasivos: 0,
    patrimonio: 0,
    ingresosDelMes: 0,
    gastosDelMes: 0,
    utilidadDelMes: 0,
    cuentasPorCobrar: 0,
    cuentasPorPagar: 0,
    ingresosVsGastos: {
      labels: [],
      ingresos: [],
      gastos: [],
    },
    gastosDistribucion: {
      labels: [],
      values: [],
    },
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(API_ENDPOINTS.CONTABILIDAD.DASHBOARD);
        setStats(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error al cargar estadísticas:", error);
        // Datos de ejemplo en caso de error para no romper la UI
        setStats({
          totalActivos: 1250000,
          totalPasivos: 450000,
          patrimonio: 800000,
          ingresosDelMes: 320000,
          gastosDelMes: 185000,
          utilidadDelMes: 135000,
          cuentasPorCobrar: 95000,
          cuentasPorPagar: 62000,
          ingresosVsGastos: {
            labels: ["Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre"],
            ingresos: [280000, 295000, 310000, 305000, 318000, 320000],
            gastos: [165000, 175000, 182000, 178000, 180000, 185000],
          },
          gastosDistribucion: {
            labels: ["Operativos", "Administrativos", "Ventas", "Financieros", "Otros"],
            values: [75000, 45000, 35000, 20000, 10000],
          },
        });
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Datos para gráfica de Ingresos vs Gastos (últimos 6 meses)
  const ingresosVsGastosData = {
    labels: stats.ingresosVsGastos.labels,
    datasets: [
      {
        label: "Ingresos",
        data: stats.ingresosVsGastos.ingresos,
        borderColor: "rgb(75, 192, 192)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Gastos",
        data: stats.ingresosVsGastos.gastos,
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Datos para Balance General (Activos vs Pasivos)
  const balanceData = {
    labels: ["Activos", "Pasivos", "Patrimonio"],
    datasets: [
      {
        data: [stats.totalActivos, stats.totalPasivos, stats.patrimonio],
        backgroundColor: [
          "rgba(54, 162, 235, 0.7)",
          "rgba(255, 99, 132, 0.7)",
          "rgba(75, 192, 192, 0.7)",
        ],
        borderColor: ["rgb(54, 162, 235)", "rgb(255, 99, 132)", "rgb(75, 192, 192)"],
        borderWidth: 2,
      },
    ],
  };

  // Datos para distribución de gastos
  const gastosDistribucionData = {
    labels: stats.gastosDistribucion.labels,
    datasets: [
      {
        data: stats.gastosDistribucion.values,
        backgroundColor: [
          "rgba(255, 99, 132, 0.7)",
          "rgba(54, 162, 235, 0.7)",
          "rgba(255, 206, 86, 0.7)",
          "rgba(75, 192, 192, 0.7)",
          "rgba(153, 102, 255, 0.7)",
        ],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
    },
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  const calcularPorcentaje = (valor, total) => {
    if (total === 0) return 0;
    return ((valor / total) * 100).toFixed(2);
  };

  return (
    <MDBox>
      {/* KPIs principales */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6} lg={3}>
          <ComplexStatisticsCard
            color="success"
            icon="account_balance_wallet"
            title="Total Activos"
            count={formatCurrency(stats.totalActivos)}
            percentage={{
              color: "success",
              amount: "+12%",
              label: "vs mes anterior",
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <ComplexStatisticsCard
            color="warning"
            icon="credit_card"
            title="Total Pasivos"
            count={formatCurrency(stats.totalPasivos)}
            percentage={{
              color: "success",
              amount: "-5%",
              label: "vs mes anterior",
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <ComplexStatisticsCard
            color="info"
            icon="trending_up"
            title="Patrimonio"
            count={formatCurrency(stats.patrimonio)}
            percentage={{
              color: "success",
              amount: "+18%",
              label: "vs mes anterior",
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <ComplexStatisticsCard
            color="primary"
            icon="attach_money"
            title="Utilidad del Mes"
            count={formatCurrency(stats.utilidadDelMes)}
            percentage={{
              color: "success",
              amount: calcularPorcentaje(stats.utilidadDelMes, stats.ingresosDelMes) + "%",
              label: "margen",
            }}
          />
        </Grid>
      </Grid>

      {/* Segunda fila de KPIs */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6} lg={3}>
          <ComplexStatisticsCard
            color="success"
            icon="trending_up"
            title="Ingresos del Mes"
            count={formatCurrency(stats.ingresosDelMes)}
            percentage={{
              color: "success",
              amount: "+8.5%",
              label: "vs mes anterior",
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <ComplexStatisticsCard
            color="error"
            icon="trending_down"
            title="Gastos del Mes"
            count={formatCurrency(stats.gastosDelMes)}
            percentage={{
              color: "error",
              amount: "+2.8%",
              label: "vs mes anterior",
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <ComplexStatisticsCard
            color="info"
            icon="request_quote"
            title="Cuentas por Cobrar"
            count={formatCurrency(stats.cuentasPorCobrar)}
            percentage={{
              color: "warning",
              amount: "23",
              label: "facturas pendientes",
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <ComplexStatisticsCard
            color="warning"
            icon="payment"
            title="Cuentas por Pagar"
            count={formatCurrency(stats.cuentasPorPagar)}
            percentage={{
              color: "info",
              amount: "15",
              label: "facturas pendientes",
            }}
          />
        </Grid>
      </Grid>

      {/* Gráficas */}
      <Grid container spacing={3}>
        {/* Ingresos vs Gastos */}
        <Grid item xs={12} lg={8}>
          <Card>
            <MDBox p={3}>
              <MDBox mb={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Ingresos vs Gastos (Últimos 6 meses)
                </MDTypography>
              </MDBox>
              <MDBox height="300px">
                <Line data={ingresosVsGastosData} options={chartOptions} />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Balance General */}
        <Grid item xs={12} lg={4}>
          <Card>
            <MDBox p={3}>
              <MDBox mb={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Composición Financiera
                </MDTypography>
              </MDBox>
              <MDBox height="300px">
                <Doughnut
                  data={balanceData}
                  options={{ ...chartOptions, maintainAspectRatio: true }}
                />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Distribución de Gastos */}
        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDBox mb={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Distribución de Gastos
                </MDTypography>
              </MDBox>
              <MDBox height="300px">
                <Doughnut data={gastosDistribucionData} options={chartOptions} />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Indicadores Financieros */}
        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDBox mb={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Indicadores Financieros
                </MDTypography>
              </MDBox>
              <MDBox>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={1}
                      borderBottom="1px solid #e0e0e0"
                    >
                      <MDTypography variant="button" fontWeight="medium">
                        Razón Corriente (Liquidez)
                      </MDTypography>
                      <MDTypography variant="button" color="success" fontWeight="bold">
                        {(stats.totalActivos / stats.totalPasivos).toFixed(2)}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={1}
                      borderBottom="1px solid #e0e0e0"
                    >
                      <MDTypography variant="button" fontWeight="medium">
                        Ratio de Endeudamiento
                      </MDTypography>
                      <MDTypography variant="button" color="warning" fontWeight="bold">
                        {calcularPorcentaje(stats.totalPasivos, stats.totalActivos)}%
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={1}
                      borderBottom="1px solid #e0e0e0"
                    >
                      <MDTypography variant="button" fontWeight="medium">
                        Margen de Utilidad Neta
                      </MDTypography>
                      <MDTypography variant="button" color="success" fontWeight="bold">
                        {calcularPorcentaje(stats.utilidadDelMes, stats.ingresosDelMes)}%
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={1}
                      borderBottom="1px solid #e0e0e0"
                    >
                      <MDTypography variant="button" fontWeight="medium">
                        ROE (Return on Equity)
                      </MDTypography>
                      <MDTypography variant="button" color="success" fontWeight="bold">
                        {calcularPorcentaje(stats.utilidadDelMes * 12, stats.patrimonio)}%
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" py={1}>
                      <MDTypography variant="button" fontWeight="medium">
                        Rotación de Cuentas por Cobrar
                      </MDTypography>
                      <MDTypography variant="button" color="info" fontWeight="bold">
                        {(stats.ingresosDelMes / stats.cuentasPorCobrar).toFixed(2)}x
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
